import React, { useState } from 'react';
import { Modal, Spin, Alert, Tabs } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { BsLightbulb, BsBook, BsHeart } from 'react-icons/bs';
import styles from './index.module.scss';
import { BiBrain } from 'react-icons/bi';

interface LearningInsightModalProps {
  visible: boolean;
  onCancel: () => void;
  analysis: any[];
  isLoading: boolean;
  error: any;
}

const LearningInsightModal: React.FC<LearningInsightModalProps> = ({
  visible,
  onCancel,
  analysis,
  isLoading,
  error,
}) => {
  const [activeTab, setActiveTab] = useState('moral');

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            size="large"
          />
          <p>Analyzing your story...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message="Error"
          description="Failed to load story analysis. Please try again."
          type="error"
          showIcon
        />
      );
    }

    if (!analysis || analysis.length === 0) {
      return (
        <div className={styles.emptyState}>
          <BsLightbulb size={48} />
          <p>No analysis available</p>
        </div>
      );
    }

    const analysisData = analysis[0]; // Get the first item from the array

    const tabItems = [
      {
        key: 'moral',
        label: (
          <span className={styles.tabLabel}>
            <BsHeart className={styles.tabIcon} />
            Moral
          </span>
        ),
        children: (
          <div className={styles.tabContent}>
            {analysisData.Moral ? (
              <div
                className={styles.analysisText}
                dangerouslySetInnerHTML={{
                  __html: analysisData.Moral.replace(/\n/g, '<br/>'),
                }}
              />
            ) : (
              <div className={styles.emptyTabContent}>
                <p>No moral analysis available</p>
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'educational',
        label: (
          <span className={styles.tabLabel}>
            <BsBook className={styles.tabIcon} />
            Educational Analysis
          </span>
        ),
        children: (
          <div className={styles.tabContent}>
            {analysisData.educational_analysis ? (
              <div
                className={styles.analysisText}
                dangerouslySetInnerHTML={{
                  __html: analysisData.educational_analysis.replace(
                    /\n/g,
                    '<br/>',
                  ),
                }}
              />
            ) : (
              <div className={styles.emptyTabContent}>
                <p>No educational analysis available</p>
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'psychological',
        label: (
          <span className={styles.tabLabel}>
            <BiBrain className={styles.tabIcon} />
            Psychological Analysis
          </span>
        ),
        children: (
          <div className={styles.tabContent}>
            {analysisData.psychological_analysis ? (
              <div
                className={styles.analysisText}
                dangerouslySetInnerHTML={{
                  __html: analysisData.psychological_analysis.replace(
                    /\n/g,
                    '<br/>',
                  ),
                }}
              />
            ) : (
              <div className={styles.emptyTabContent}>
                <p>No psychological analysis available</p>
              </div>
            )}
          </div>
        ),
      },
    ];

    return (
      <div className={styles.analysisContent}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles.analysisTabs}
        />
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
      className={styles.learningInsightModal}
      title="Learning Insights">
      {renderContent()}
    </Modal>
  );
};

export default LearningInsightModal;
